import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Lesson } from '@mira-avant/mtlibs';
import { LessonCreatePayload, LessonUpdatePayload } from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { ActivityService } from './activity.service';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class LessonService {
	private readonly BASE_PATH = 'lessons';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public lessons$: BehaviorSubject<Lesson[]> = new BehaviorSubject<Lesson[]>([]);

	constructor(
		private api: ApiService,
		private languageService: LanguageService,
		private activityService: ActivityService
	) {}

	public async onUnitChange$(unitUuid: string | undefined): Promise<void> {
		this.lessons$.next([]);

		if (!unitUuid) {
			return;
		}

		await this.getByUnit$(unitUuid);
	}

	public setActiveText(lesson: Lesson): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;
		const activeText = lesson.lesson_text.find(lessonText => lessonText.default_lang_id === currentLang);
		lesson.active_text = activeText || lesson.lesson_text[0];
	}

	public processLesson(lesson: Lesson): Lesson {
		this.setActiveText(lesson);
		if (lesson.activity && Array.isArray(lesson.activity)) {
			lesson.activity = this.activityService.processActivities(lesson.activity);
		}
		return lesson;
	}

	public processLessons(lessons: Lesson[]): Lesson[] {
		return this.sortLessons(lessons.map(lesson => this.processLesson(lesson)));
	}

	public sortLessons(lessons: Lesson[]): Lesson[] {
		return lessons.sort((a: Lesson, b: Lesson) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Lesson> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const lesson = await firstValueFrom(
				this.api.get<Lesson>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params })
			);
			return this.processLesson(lesson);
		});
	}

	public async create$(lessonData: LessonCreatePayload): Promise<Lesson> {
		return this.withLoading(async () => {
			const newLesson = await firstValueFrom(this.api.post<Lesson>(this.BASE_PATH, lessonData));
			const processedLesson = this.processLesson(newLesson);
			const currentLessons = this.lessons$.value;
			const updatedLessons = this.processLessons([...currentLessons, processedLesson]);
			this.lessons$.next(updatedLessons);
			return processedLesson;
		});
	}

	public async update$(uuid: string, lessonData: LessonUpdatePayload): Promise<Lesson> {
		return this.withLoading(async () => {
			const updatedLesson = await firstValueFrom(this.api.put<Lesson>(`${this.BASE_PATH}/${uuid}`, lessonData));
			const processedLesson = this.processLesson(updatedLesson);
			const currentLessons = this.lessons$.value;
			const updatedLessons = this.processLessons(
				currentLessons.map(lesson => (lesson.uuid === uuid ? processedLesson : lesson))
			);
			this.lessons$.next(updatedLessons);
			return processedLesson;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));
			const currentLessons = this.lessons$.value;
			const updatedLessons = currentLessons.filter(lesson => lesson.uuid !== uuid);
			this.lessons$.next(updatedLessons);
		});
	}

	public async getByUnit$(
		unitUuid: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Lesson[]> {
		return this.withLoading(async () => {
			const params = {
				unitUuid,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const lessons = await firstValueFrom(this.api.get<Lesson[]>(`${this.BASE_PATH}/unit`, { params }));
			const processedLessons = this.processLessons(lessons);
			this.lessons$.next(processedLessons);
			return processedLessons;
		});
	}

	public async duplicate$(uuid: string, unitParentUuid: string): Promise<Lesson> {
		return this.withLoading(async () => {
			const duplicatedLesson = await firstValueFrom(
				this.api.post<Lesson>(`${this.BASE_PATH}/duplicate/${uuid}/${unitParentUuid}`, {})
			);
			const processedLesson = this.processLesson(duplicatedLesson);
			const currentLessons = this.lessons$.value;
			const updatedLessons = this.processLessons([...currentLessons, processedLesson]);
			this.lessons$.next(updatedLessons);
			return processedLesson;
		});
	}

	public async duplicateBulk$(uuids: string[], unitParentUuid: string): Promise<Lesson[]> {
		return this.withLoading(async () => {
			const duplicatedLessons = await firstValueFrom(
				this.api.post<Lesson[]>(`${this.BASE_PATH}/duplicate-bulk/${unitParentUuid}`, { uuids })
			);
			const processedLessons = this.processLessons(duplicatedLessons);
			const currentLessons = this.lessons$.value;
			const newUuidSet = new Set(processedLessons.map(lesson => lesson.uuid));
			const filteredCurrent = currentLessons.filter(lesson => !newUuidSet.has(lesson.uuid));
			const updatedLessons = this.processLessons([...processedLessons, ...filteredCurrent]);
			this.lessons$.next(updatedLessons);
			return processedLessons;
		});
	}

	public async moveUp$(uuid: string, unitUuid: string): Promise<Lesson[]> {
		return this.withLoading(async () => {
			const updatedLessons = await firstValueFrom(
				this.api.post<Lesson[]>(`${this.BASE_PATH}/move-up/${uuid}/${unitUuid}`, {})
			);
			const processedLessons = this.processLessons(updatedLessons);
			this.lessons$.next(processedLessons);
			return processedLessons;
		});
	}

	public async moveDown$(uuid: string, unitUuid: string): Promise<Lesson[]> {
		return this.withLoading(async () => {
			const updatedLessons = await firstValueFrom(
				this.api.post<Lesson[]>(`${this.BASE_PATH}/move-down/${uuid}/${unitUuid}`, {})
			);
			const processedLessons = this.processLessons(updatedLessons);
			this.lessons$.next(processedLessons);
			return processedLessons;
		});
	}
}
